import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import classNames from 'classnames'
import React, { type JSX } from 'react'
import { useTranslation } from 'react-i18next'

import { useAccountReadContext } from '@context/AccountContextContext'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Body1, Heading3, Heading6 } from '@ui/Typography'
import { PageSection } from '@ui/components/PageSection'
import { RichIcon } from '@ui/components/RichIcon'
import { CheckIcon } from '@ui/icons/Check.icon'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import useSensitiveDataFlag from '@utils/useSensitiveDataFlag'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { Box } from '@shared/components/Box'
import { CopyButton } from '@shared/components/CopyButton'
import { Loading } from '@shared/components/Loading/Loading'

import styles from './ProfilePage.module.scss'

export const ProfilePage = (): JSX.Element => {
  const apiClient = useApiClient(ClientApiClient)
  const { account } = useAccountReadContext()

  const { t } = useTranslation()
  const sensitiveDataFlag = useSensitiveDataFlag()
  const { formatMoney } = useFormatNumber()

  const { data: wallets, isLoading: isLoadingWallets } = useQuery({
    queryKey: queryKeys.paymentAgent.wallets(),
    queryFn: () => apiClient.getWallets({ walletTypeId: 100 }),
    refetchOnMount: 'always',
  })

  const { data: agent, isLoading: isLoadingAgent } = useQuery({
    queryKey: queryKeys.paymentAgent.info(),
    queryFn: () => apiClient.getPaymentAgent(account!.clientPaymentAgent!.id),
    enabled: !!account?.clientPaymentAgent?.id,
  })
  const firstWallet = wallets?.[0]

  return (
    <Loading showLoadingIcon isLoading={isLoadingAgent && isLoadingWallets}>
      <PageSection title={t('Payment Agent.Payment Agent(PA) Profile Info')} />
      <div className={styles.profileWrapper}>
        <div className={styles.logoWrapper}>
          <Heading3>
            <SensitiveData>{agent?.externalName}</SensitiveData>
          </Heading3>
          <Box flex gap='x16' my='x32'>
            <RichIcon
              shape='circle'
              icon={{ icon: CheckIcon, color: 'text-white' }}
              size={'x24'}
              bg='surface-success-high-em'
            />
            <Body1 isParagraph className={styles.grayText}>
              {t('Payment Agent.Verified Agent')}
            </Body1>
          </Box>
        </div>
        <div className={styles.infoWrapper}>
          <Heading6 medium>{t('Payment Agent.Main info')}</Heading6>
          <div className={styles.row}>
            <Body1 isParagraph className={styles.grayText}>
              {t('Payment Agent.Balance')}
            </Body1>
            <Body1 isParagraph medium>
              <SensitiveData>
                {wallets && formatMoney(firstWallet?.balance, firstWallet?.currency.id)}
              </SensitiveData>
            </Body1>
          </div>
          <div className={styles.row}>
            <Body1 isParagraph className={styles.grayText}>
              {t('Payment Agent.Website')}
            </Body1>
            <div className='is-flex'>
              <Body1 isParagraph className={styles.link}>
                <SensitiveData>{agent?.website}</SensitiveData>
              </Body1>
            </div>
          </div>

          {agent && agent.profiles.length > 0 && (
            <div className={styles.profile}>
              <Heading6 medium>{t('Payment Agent.Responsible person')} #1</Heading6>
              <div className={styles.row}>
                <Body1 isParagraph className={styles.grayText}>
                  {t('Payment Agent.Full Name')}
                </Body1>
                <Body1 isParagraph medium>
                  <SensitiveData>
                    {agent.profiles[0].firstName} {agent.profiles[0].lastName}
                  </SensitiveData>
                </Body1>
              </div>
              <div className={styles.row}>
                <Body1 isParagraph className={styles.grayText}>
                  {t('Payment Agent.Email')}
                </Body1>
                <div className='is-flex'>
                  <Body1 isParagraph className={classNames(styles.flexText, 'is-link')}>
                    <SensitiveData> {agent.profiles[0].email}</SensitiveData>
                  </Body1>
                  <CopyButton value={agent.profiles[0].email} disabled={sensitiveDataFlag} />
                </div>
              </div>

              {!!agent.profiles.length && (
                <div className={styles.row}>
                  <Body1 isParagraph className={styles.grayText}>
                    {t('Payment Agent.Phone')}
                  </Body1>
                  <div className='is-flex'>
                    <Body1 isParagraph className={styles.flexText}>
                      <SensitiveData>{agent.profiles[0]?.phone}</SensitiveData>
                    </Body1>
                    <CopyButton
                      value={agent.profiles[0]?.phone ?? ''}
                      disabled={sensitiveDataFlag}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {agent && agent.profiles.length > 1 && (
            <div className={styles.profile}>
              <Heading6 medium>{t('Payment Agent.Responsible person')} #2</Heading6>
              <div className={styles.row}>
                <Body1 className={styles.grayText}>{t('Payment Agent.Full Name')}</Body1>
                <Body1 isParagraph medium>
                  <SensitiveData>
                    {agent.profiles[1].firstName} {agent.profiles[1].lastName}
                  </SensitiveData>
                </Body1>
              </div>
              <div className={styles.row}>
                <Body1 isParagraph className={styles.grayText}>
                  {t('Payment Agent.Email')}
                </Body1>
                <div className='is-flex'>
                  <Body1 isParagraph className={classNames(styles.flexText, 'is-link')}>
                    <SensitiveData>{agent.profiles[1].email}</SensitiveData>
                  </Body1>
                  <CopyButton value={agent.profiles[1].email} disabled={sensitiveDataFlag} />
                </div>
              </div>

              <div className={styles.row}>
                <Body1 isParagraph className={styles.grayText}>
                  {t('Payment Agent.Phone')}
                </Body1>
                <div className='is-flex'>
                  <Body1 isParagraph className={styles.flexText}>
                    <SensitiveData>{agent.profiles[1].phone}</SensitiveData>
                  </Body1>
                  <CopyButton value={agent.profiles[1].phone} disabled={sensitiveDataFlag} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Loading>
  )
}
